import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { DataTable } from 'motili-ui';
import {
    WorkOrderFilters,
    TableRecords,
} from '../../models/KeyWorkOrderStatusesModel';
import { TableHeaders } from '../../models/WidgetModels';
import { useGetFilteredWorkOrdersQuery } from '../../store/api/KeyWorkOrderStatusesWidgetApi';
import {
    snakeCaseToTitleCase,
    shortenedAddress,
} from '../../../utils/WidgetUtils';
import { PaginationInline, ShowingInline } from '../../components/paging/Paging';

const MIN_TABLE_WIDTH = 1200;

const raf = () => new Promise<void>(r => requestAnimationFrame(() => r()));
async function waitForStable(el: HTMLElement) {
    for (let i = 0; i < 40; i++) {
        const w1 = el.clientWidth;
        const o1 = getComputedStyle(el).opacity;
        await raf(); await raf();
        const w2 = el.clientWidth;
        const hasHead = !!el.querySelector('thead th');
        const hasRow  = !!el.querySelector('tbody tr');
        if (o1 === '1' && w1 === w2 && w2 > 0 && hasHead && hasRow) return;
    }
}

const setImp = (s: CSSStyleDeclaration, k: string, v: string) =>
    s.setProperty(k, v, 'important');

function normalizeTbody(root: HTMLElement) {
    root.querySelectorAll('table.motili-data-table > tbody').forEach((tb) => {
        const el = tb as HTMLElement;
        el.classList.remove('iyxFJp');
        setImp(el.style, 'float', 'none');
        setImp(el.style, 'display', 'table-row-group');
        setImp(el.style, 'line-height', 'normal');
        setImp(el.style, 'font-style', 'normal');
        setImp(el.style, 'clear', 'none');
        setImp(el.style, 'position', 'static');
        setImp(el.style, 'width', 'auto');
    });
}

function relaxFlexAncestors(node: HTMLElement) {
    let el: HTMLElement | null = node;
    for (let i = 0; i < 6 && el; i++) {
        const p = el.parentElement as HTMLElement | null;
        if (!p) break;
        const cs = getComputedStyle(p);
        if (cs.display === 'flex' || cs.display === 'inline-flex') {
            setImp(p.style, 'min-width', '0');
            setImp(p.style, 'overflow', 'visible');
        }
        el = p;
    }
}

function forceHorizontalOnlyScroll(root: HTMLElement) {
    const scroller = root.closest('.ef-table-scroll') as HTMLElement | null;
    const table = root.querySelector('table') as HTMLTableElement | null;
    if (!scroller || !table) return;
    setImp(scroller.style, 'min-width', '0');
    setImp(scroller.style, 'width', '100%');
    setImp(scroller.style, 'overflow-x', 'auto');
    setImp(scroller.style, 'overflow-y', 'hidden');
    setImp(scroller.style, 'max-height', 'none');
    setImp(scroller.style, '-webkit-overflow-scrolling', 'touch');
    setImp(scroller.style, 'scrollbar-gutter', 'stable');
    setImp(scroller.style, 'background', '#fff');
    setImp(scroller.style, 'border-radius', '0');

    relaxFlexAncestors(scroller);

    // ----- TABLE CORE -----
    table.classList.remove('table', 'table-hover');
    table.classList.add('motili-data-table');
    setImp(table.style, 'table-layout', 'fixed');
    setImp(table.style, 'border-collapse', 'collapse');
    setImp(table.style, 'border-spacing', '0');
    setImp(table.style, 'width', 'auto');
    setImp(table.style, 'min-width', `${MIN_TABLE_WIDTH}px`);

    table.querySelectorAll<HTMLTableCellElement>('thead th').forEach(th => {
        setImp(th.style, 'background', '#0E2248');
        setImp(th.style, 'color', '#FFFFFF');
        setImp(th.style, 'vertical-align', 'middle');
        setImp(th.style, 'padding', '8px 12px');
        setImp(th.style, 'line-height', '1.3');
        setImp(th.style, 'box-sizing', 'border-box');
    });

    // body cells: nowrap + ellipsis
    table.querySelectorAll<HTMLTableCellElement>('th, td').forEach(cell => {
        setImp(cell.style, 'padding', '8px 12px');
        setImp(cell.style, 'line-height', '1.4');
        setImp(cell.style, 'box-sizing', 'border-box');
        setImp(cell.style, 'white-space', 'nowrap');
        setImp(cell.style, 'overflow', 'hidden');
        setImp(cell.style, 'text-overflow', 'ellipsis');
    });

    table.querySelectorAll<HTMLTableCellElement>('tbody td').forEach(td => {
        setImp(td.style, 'vertical-align', 'top');
        Array.from(td.children).forEach((child) => {
            const el = child as HTMLElement;
            setImp(el.style, 'display', 'inline-flex');
            setImp(el.style, 'align-items', 'center');
            setImp(el.style, 'gap', '6px');
            setImp(el.style, 'margin', '0');
            setImp(el.style, 'padding', '0');
            setImp(el.style, 'max-width', '100%');
            setImp(el.style, 'white-space', 'inherit');
            setImp(el.style, 'line-height', 'inherit');
            setImp(el.style, 'overflow', 'hidden');
            setImp(el.style, 'text-overflow', 'ellipsis');
        });
    });
}
interface KeyWorkOrderStatusTableProps {
    mappedWorkOrderCountData:
        | { label: string; value: string; count: number }[]
        | undefined;
    activeTablePage: number;
    onPageClick: (e: number) => void;
    workOrderFilters: WorkOrderFilters;
    chartSelectedValue: string;
    recordsPerPage: number;
}

export function KeyWorkOrderStatusesTable(
    props: Readonly<KeyWorkOrderStatusTableProps>
): JSX.Element {
    const {
        mappedWorkOrderCountData,
        activeTablePage,
        onPageClick,
        workOrderFilters,
        chartSelectedValue,
        recordsPerPage,
    } = props;

    const tableHeaders: TableHeaders[] = [
        {
            displayName: 'Number',
            value: 'id',
            sortable: false,
            ascending: false,
        },
        {
            displayName: 'Type',
            value: 'workOrderTypeId',
            sortable: false,
            ascending: false,
        },
        {
            displayName: 'Client',
            value: 'client',
            sortable: false,
            ascending: false,
        },
        {
            displayName: 'Address',
            value: 'stdAddress',
            sortable: false,
            ascending: false,
        },
        {
            displayName: 'State',
            value: 'stdRegion',
            sortable: false,
            ascending: false,
        },
        {
            displayName: 'Contractor Company',
            value: 'contractor',
            sortable: false,
            ascending: false,
        },
        {
            displayName: 'Reference 1',
            value: 'accountManager',
            sortable: false,
            ascending: false,
        },
        {
            displayName: 'Since Creation',
            value: 'createdDate',
            sortable: false,
            ascending: false,
        },
    ];

    // Api call to get work order table data
    const { data: GetFilteredWorkOrdersData } = useGetFilteredWorkOrdersQuery(
        {
            ...workOrderFilters,
            page: activeTablePage - 1,
            statusIds: [chartSelectedValue],
        },
        { refetchOnMountOrArgChange: true }
    );
    const totalNumberOfRecords:
        | {
              label: string;
              value: string;
              count: number;
          }[]
        | undefined = mappedWorkOrderCountData?.filter(
        obj => obj.value === chartSelectedValue
    );
    const totalCount = useMemo(() => {
        return mappedWorkOrderCountData?.find(o => o.value === chartSelectedValue)?.count ?? 0;
    }, [mappedWorkOrderCountData, chartSelectedValue]);

    const mappedWorkOrderData: TableRecords[] = useMemo(() => {
        if (GetFilteredWorkOrdersData) {
            return GetFilteredWorkOrdersData.result.map((record, index) => {
                const numberLink: string = `/#/ops/work-orders/${record.id}/details`;
                const clientLink: string = `/#/ops/clients/${record.client.id}/details`;
                const contractorLink: string = `/#/ops/contractors/${record.contractor?.id}/details`;
                const workOrderTypeDisplay: string = snakeCaseToTitleCase(
                    record.workOrderTypeId
                );
                const stdAddress: string = shortenedAddress(
                    record.property.stdAddress
                );
                const extReference1: string = record.extReferenceNumber1
                    ? record.extReferenceNumber1
                    : 'No Reference';
                const givenDateObj: any = new Date(record.created);
                const todayDateObj: any = new Date();
                const timeDifference: number = givenDateObj - todayDateObj;
                const differenceInDays: number = Math.round(
                    timeDifference / (1000 * 60 * 60 * 24)
                );

                return {
                    id: (
                        <a
                            data-test-id={`KWOSTableIdLink${index}`}
                            href={numberLink}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                        >
                            {record.number}
                        </a>
                    ),
                    workOrderTypeId: workOrderTypeDisplay,
                    client: (
                        <a
                            data-test-id={`KWOSTableClientLink${index}`}
                            href={clientLink}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                        >
                            {record.client.name}
                        </a>
                    ),
                    stdAddress: stdAddress,
                    stdRegion: record.property.stdRegion,
                    contractor: record.contractor?.name ? (
                        <a
                            data-test-id={`KWOSTableContractorLink${index}`}
                            href={contractorLink}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                        >
                            {record.contractor?.name}
                        </a>
                    ) : (
                        'NCF'
                    ),
                    accountManager: extReference1,
                    createdDate: `${Math.abs(differenceInDays)} Days`,
                };
            });
        }
        return [];
    }, [GetFilteredWorkOrdersData]);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const wrapperWidth = 0;
    const hostRef = useRef<HTMLDivElement | null>(null);
    const dataTableKey = useMemo(
        () => `ef-${chartSelectedValue}-${activeTablePage}-${recordsPerPage}-${wrapperWidth}`,
        [chartSelectedValue, activeTablePage, recordsPerPage, wrapperWidth]
    );
    // Wait for the container to stabilize, then remove Bootstrap classes and apply inline styles
    useLayoutEffect(() => {
        const root = hostRef.current;
        if (!root) return;

        const apply = () => {
            const table = root.querySelector('table');
            if (!table) return;
            table.classList.remove('table', 'table-hover');
            table.classList.add('motili-data-table');
            normalizeTbody(root);
            forceHorizontalOnlyScroll(root);
        };

        let cancelled = false;
        (async () => { await waitForStable(root); if (!cancelled) apply(); })();

        const mo = new MutationObserver(() => {
            apply();
        });
        mo.observe(root, { childList: true, subtree: true });

        return () => { cancelled = true; mo.disconnect(); };
    }, [dataTableKey]);

    return (
        <div className="ef-scope" ref={wrapperRef} style={{ width: '100%' }}>
            <div className="ef-table-scroll">
                <div className="ef-host" ref={hostRef}>
                    <DataTable
                        headerBackground={'#0E2248'}
                        headerTextColor={'#FFFFFF'}
                        data-test-id={'KWOSWidgetClosedTable'}
                        totalRecordCount={
                            totalNumberOfRecords ? totalNumberOfRecords[0].count : 0
                        }
                        records={mappedWorkOrderData}
                        tableHeaders={tableHeaders}
                        showPagination={false}
                        activePage={activeTablePage}
                        recordsPerPage={recordsPerPage}
                        onPageClick={pageNumber => {
                            onPageClick(pageNumber);
                        }}
                        onListItemClick={listItem => {}}
                        onTableHeaderItemClick={headerItem => {}}
                    />
                </div>
            </div>
            <div style={{  display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>
                <ShowingInline
                    total={totalCount || 0}
                    page={activeTablePage}
                    perPage={recordsPerPage}
                />
                <div style={{ flex: 1 }} />
                <PaginationInline
                    total={totalCount || 0}
                    page={activeTablePage}
                    perPage={recordsPerPage}
                    onChange={onPageClick}
                />
            </div>
        </div>
    );
}
